/* Выбор голоса и пример звучания для экранов движка (engine-kit, план M3 §2 S5): ядро, взятое из voice-server.ts (pickVoice, sample),
   для «Другой голос», голоса героя, «Голос из каталога» и «Мой голос · клон» экранов M3.
   Правила (как у родных выборов: use-voice-picker.ts, circle/CircleSettingsCard.tsx, cover/CoverOrderForm.tsx):
   - сведения о голосе подтверждает сервер (GET /app/catalog/voices/{id}); id ответа сверяется (иначе voice_mismatch); переозвучку записи
     умеет только голос с is_s2s === true; новый выбор прерывает прежний запрос, а опоздавший ответ прежнего выбора ничего не делает;
   - свой клон с переозвучкой — первый готовый (status ready, is_s2s, есть voice_id каталога) из GET /app/voices; сбой — клона нет;
   - пример звучания — один на кадр: Audio по безопасному адресу (safeMediaUrl), новый пример гасит прежний, конец или отказ браузера — onEnd. */
import { endpoints } from '../../../api';
import type { Voice } from '../../../api/types';
import type { UserVoice } from '../../../api/user-voices';
import { safeMediaUrl } from '../studio-content';

/** Сведения голоса для выбора: GET /app/catalog/voices/{id}; id ответа должен совпасть (иначе voice_mismatch) */
export async function voiceFor(id: number, signal?: AbortSignal): Promise<Voice> {
  const details = await endpoints.voiceDetails(id, signal);
  if (details.id !== id) throw new Error('voice_mismatch');
  return details;
}

export interface VoicePick {
  /** Новый выбор голоса id (прежний запрос прерывается): voice — сведения сервера, ok — голос годится (с s2s — только is_s2s === true).
      null — выбор устарел (прервали или выбрали другой); сбой этого выбора — исключение */
  pick(id: number, o?: { s2s?: boolean }): Promise<{ voice: Voice; ok: boolean } | null>;
  /** Прервать идущий выбор (кадр сменился или закрылся) */
  abort(): void;
  /** Идёт запрос выбора */
  busy(): boolean;
}
export function createVoicePick(): VoicePick {
  let request: AbortController | null = null;
  return {
    async pick(id, o = {}) {
      request?.abort();
      const mine = new AbortController();
      request = mine;
      const stale = () => mine.signal.aborted || request !== mine;
      try {
        const voice = await voiceFor(id, mine.signal);
        if (stale()) return null;
        return { voice, ok: !o.s2s || voice.is_s2s === true };
      } catch (failure) {
        if (stale()) return null;
        throw failure;
      } finally {
        if (request === mine) request = null;
      }
    },
    abort() {
      request?.abort();
      request = null;
    },
    busy: () => request !== null,
  };
}

/** Свой готовый клон с переозвучкой («Мой голос · клон»): первый из GET /app/voices со status ready, is_s2s и voice_id; нет или сбой — null */
export async function readyClone(signal?: AbortSignal): Promise<UserVoice | null> {
  try {
    const page = await endpoints.userVoices.list(undefined, signal);
    return page.items.find(voice => voice.status === 'ready' && voice.is_s2s && !!voice.voice_id) ?? null;
  } catch {
    return null;
  }
}

export interface VoiceSample {
  /** Пример по адресу url (прежний гаснет): true — пошёл; false — адрес небезопасен или пуст. onEnd — дослушали или браузер не пустил */
  play(url: unknown, onEnd: () => void): boolean;
  /** Погасить пример (onEnd не зовётся) */
  stop(): void;
  /** Пример звучит */
  active(): boolean;
}
export function createVoiceSample(): VoiceSample {
  let audio: HTMLAudioElement | null = null;
  function stop() {
    if (!audio) return;
    audio.onended = null;
    audio.pause();
    audio = null;
  }
  return {
    play(url, onEnd) {
      stop();
      const safe = safeMediaUrl(url);
      if (!safe) return false;
      const playing = new Audio(safe);
      audio = playing;
      const end = () => {
        if (audio !== playing) return;
        audio = null;
        onEnd();
      };
      playing.onended = end;
      playing.play().catch(end);
      return true;
    },
    stop,
    active: () => audio !== null,
  };
}
